/* ARRANGE YOUR OWN MENU (Jay Jung, 2026-10-21: "make its menu order customized by users (drag & drop)").

   Drawn in place of the sidebar while somebody arranges it, not on top of the live menu: a menu whose entries can
   be dragged is a menu whose entries a slightly long click drags instead of opens. Folders move among folders,
   entries move inside their own folder (menuOrder.js says why). Every move has ↑/↓ beside the drag — a control
   reachable only by dragging is reachable by nobody on a keyboard, and the arrows are what a check presses.
   Nothing is saved until Done; Cancel leaves the menu as it was; Reset puts the portal's own order back.
   The folders open FOLDED: twelve folders and thirty-seven entries open at once is a panel two screens long, and
   moving a folder from the bottom to the top would mean dragging it the whole way. A folder opens to arrange what
   is inside it. */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ChevronRight, GripVertical } from 'lucide-react';
import { menuKeyOf } from './menuOrder';
import './SidebarArrange.css';

const move = (list, from, to) => {
    if (from === to || to < 0 || to >= list.length) return list;
    const next = list.slice();
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    return next;
};

export default function SidebarArrange({ folders, onSave, onReset, onCancel, customised }) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState(folders);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [openIds, setOpenIds] = useState(() => new Set());
    const toggle = (id) => setOpenIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const drag = useRef(null);          // { type: 'folder' | 'item', folderId, key }

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onCancel]);

    const moveFolder = (from, to) => setDraft((d) => move(d, from, to));
    const moveItem = (folderId, from, to) => setDraft((d) => d.map((f) => (
        f.id === folderId ? { ...f, items: move(f.items, from, to) } : f)));

    // Live: the row follows the pointer while it is dragged, as the GTM lists do.
    const overFolder = (e, overId) => {
        const g = drag.current;
        if (!g || g.type !== 'folder') return;
        e.preventDefault();
        if (g.folderId === overId) return;
        setDraft((d) => move(d, d.findIndex((f) => f.id === g.folderId), d.findIndex((f) => f.id === overId)));
    };
    const overItem = (e, folderId, overKey) => {
        const g = drag.current;
        if (!g || g.type !== 'item' || g.folderId !== folderId) return;
        e.preventDefault();
        e.stopPropagation();
        if (g.key === overKey) return;
        setDraft((d) => d.map((f) => {
            if (f.id !== folderId) return f;
            const keys = f.items.map(menuKeyOf);
            return { ...f, items: move(f.items, keys.indexOf(g.key), keys.indexOf(overKey)) };
        }));
    };
    const start = (e, g) => {
        e.stopPropagation();
        drag.current = g;
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', g.key || g.folderId); } catch (x) { /* some browsers refuse */ }
    };
    const end = () => { drag.current = null; };

    const changed = JSON.stringify(draft.map((f) => [f.id, f.items.map(menuKeyOf)]))
        !== JSON.stringify(folders.map((f) => [f.id, f.items.map(menuKeyOf)]));

    const run = async (fn) => {
        setBusy(true); setErr('');
        try { await fn(); } catch (e) { setErr(e.message || t('nav.arrangeFailed')); setBusy(false); }
    };

    return (
        <div className="sb-arrange" data-testid="sidebar-arrange">
            <div className="sb-arr-head">
                <b>{t('nav.arrangeTitle')}</b>
                <span>{t('nav.arrangeHint')}</span>
            </div>
            {draft.map((f, fi) => (
                <section key={f.id} className="sb-arr-folder" data-folder={f.id}
                    onDragOver={(e) => overFolder(e, f.id)} onDrop={(e) => { e.preventDefault(); end(); }}>
                    <div className="sb-arr-fhead" draggable={!busy}
                        onDragStart={(e) => start(e, { type: 'folder', folderId: f.id })} onDragEnd={end}>
                        <GripVertical size={16} className="sb-arr-grip" aria-hidden="true" />
                        <button type="button" className={`sb-arr-open ${openIds.has(f.id) ? 'is-open' : ''}`}
                            aria-expanded={openIds.has(f.id)} onClick={() => toggle(f.id)}
                            title={t(openIds.has(f.id) ? 'nav.arrangeFold' : 'nav.arrangeUnfold', { name: f.label })}>
                            <ChevronRight size={15} />
                        </button>
                        {/* The name alone: with a grip, a fold and two arrows on a 240px row, the folder's icon and a
                            count left "Devel…" and "Costi…" — a list you arrange by name has to show the name. */}
                        <span className="sb-arr-label" title={`${f.label} (${f.items.length})`}>{f.label}</span>
                        <button type="button" className="sb-arr-mv" disabled={busy || fi === 0}
                            aria-label={t('nav.arrangeUp', { name: f.label })} title={t('nav.arrangeUp', { name: f.label })}
                            onClick={() => moveFolder(fi, fi - 1)}><ArrowUp size={14} /></button>
                        <button type="button" className="sb-arr-mv" disabled={busy || fi === draft.length - 1}
                            aria-label={t('nav.arrangeDown', { name: f.label })} title={t('nav.arrangeDown', { name: f.label })}
                            onClick={() => moveFolder(fi, fi + 1)}><ArrowDown size={14} /></button>
                    </div>
                    {openIds.has(f.id) && <ul className="sb-arr-items">
                        {f.items.map((it, ii) => {
                            const key = menuKeyOf(it);
                            return (
                                <li key={key} className="sb-arr-item" data-key={key} draggable={!busy}
                                    onDragStart={(e) => start(e, { type: 'item', folderId: f.id, key })} onDragEnd={end}
                                    onDragOver={(e) => overItem(e, f.id, key)}>
                                    <GripVertical size={14} className="sb-arr-grip" aria-hidden="true" />
                                    <span className="sb-arr-label">{it.label}</span>
                                    <button type="button" className="sb-arr-mv" disabled={busy || ii === 0}
                                        aria-label={t('nav.arrangeUp', { name: it.label })} title={t('nav.arrangeUp', { name: it.label })}
                                        onClick={() => moveItem(f.id, ii, ii - 1)}><ArrowUp size={13} /></button>
                                    <button type="button" className="sb-arr-mv" disabled={busy || ii === f.items.length - 1}
                                        aria-label={t('nav.arrangeDown', { name: it.label })} title={t('nav.arrangeDown', { name: it.label })}
                                        onClick={() => moveItem(f.id, ii, ii + 1)}><ArrowDown size={13} /></button>
                                </li>
                            );
                        })}
                    </ul>}
                </section>
            ))}
            {err && <div className="sb-arr-err" role="alert">{err}</div>}
            <div className="sb-arr-foot">
                <button type="button" className="sb-arr-btn" disabled={busy || !customised}
                    title={customised ? t('nav.arrangeResetHint') : t('nav.arrangeIsDefault')}
                    onClick={() => run(onReset)}>{t('nav.arrangeReset')}</button>
                <span className="sb-arr-gap" />
                <button type="button" className="sb-arr-btn" disabled={busy} onClick={onCancel}>{t('nav.arrangeCancel')}</button>
                <button type="button" className="sb-arr-btn sb-arr-done" disabled={busy}
                    onClick={() => (changed ? run(() => onSave(draft)) : onCancel())}>{t('nav.arrangeDone')}</button>
            </div>
        </div>
    );
}
